import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSystemConfig } from '@/hooks/useConfig'
import { usePlans } from '@/hooks/usePlans'
import { api } from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { KB_CEILING_FILE_MB, KB_CEILING_ROWS, KB_CEILING_PER_USER, KB_CONFIG_KEYS } from '@/types'
import { Save, Loader2, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'

const [KEY_MAX_FILE_MB, KEY_MAX_ROWS, KEY_MAX_PER_USER, KEY_ALLOWED_PLANS] = KB_CONFIG_KEYS

function sortedEqual(a: string[], b: string[]): boolean {
  const sa = [...a].sort()
  const sb = [...b].sort()
  return JSON.stringify(sa) === JSON.stringify(sb)
}

export const KbLimitsConfig = () => {
  const queryClient = useQueryClient()
  const { data: configs, isLoading } = useSystemConfig()
  const { data: plansData } = usePlans()
  const [saving, setSaving] = useState(false)

  const planOptions = (plansData ?? []).map((p: { name: string }) => p.name)

  const findValue = <T,>(key: string, fallback: T): T => {
    const found = configs?.find((c) => c.key === key)
    return found ? (found.value as T) : fallback
  }

  const original = {
    maxFileMb: findValue<number>(KEY_MAX_FILE_MB, 10),
    maxRows: findValue<number>(KEY_MAX_ROWS, 50000),
    maxPerUser: findValue<number>(KEY_MAX_PER_USER, 5),
    allowedPlans: findValue<string[]>(KEY_ALLOWED_PLANS, []),
  }

  const [maxFileMb, setMaxFileMb] = useState<number>(original.maxFileMb)
  const [maxRows, setMaxRows] = useState<number>(original.maxRows)
  const [maxPerUser, setMaxPerUser] = useState<number>(original.maxPerUser)
  const [allowedPlans, setAllowedPlans] = useState<string[]>(original.allowedPlans)
  const [error, setError] = useState('')

  const hasChanges =
    !!configs &&
    (maxFileMb !== original.maxFileMb ||
      maxRows !== original.maxRows ||
      maxPerUser !== original.maxPerUser ||
      !sortedEqual(allowedPlans, original.allowedPlans))

  useEffect(() => {
    // Only re-initialize from server data when there are no unsaved edits —
    // otherwise an unrelated config save elsewhere on SettingsPage
    // invalidates the shared ['config'] query and silently wipes
    // in-progress KB-limit edits on refetch.
    if (!configs || hasChanges) return
    setMaxFileMb(findValue<number>(KEY_MAX_FILE_MB, 10))
    setMaxRows(findValue<number>(KEY_MAX_ROWS, 50000))
    setMaxPerUser(findValue<number>(KEY_MAX_PER_USER, 5))
    setAllowedPlans(findValue<string[]>(KEY_ALLOWED_PLANS, []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs, hasChanges])

  const togglePlan = (plan: string) => {
    setAllowedPlans((plans) =>
      plans.includes(plan) ? plans.filter((p) => p !== plan) : [...plans, plan]
    )
  }

  const handleSave = async () => {
    setError('')

    if (!Number.isFinite(maxFileMb) || maxFileMb < 1 || maxFileMb > KB_CEILING_FILE_MB) {
      setError(`El tamaño máximo debe estar entre 1 y ${KB_CEILING_FILE_MB} MB`)
      return
    }
    if (!Number.isFinite(maxRows) || maxRows < 1 || maxRows > KB_CEILING_ROWS) {
      setError(`Las filas máximas deben estar entre 1 y ${KB_CEILING_ROWS.toLocaleString('es-ES')}`)
      return
    }
    if (!Number.isFinite(maxPerUser) || maxPerUser < 1 || maxPerUser > KB_CEILING_PER_USER) {
      setError(`El máximo por usuario debe estar entre 1 y ${KB_CEILING_PER_USER}`)
      return
    }
    if (allowedPlans.length === 0) {
      setError('Selecciona al menos un plan habilitado')
      return
    }

    const updates: Array<{ key: string; value: number | string[] }> = []
    if (maxFileMb !== original.maxFileMb) updates.push({ key: KEY_MAX_FILE_MB, value: maxFileMb })
    if (maxRows !== original.maxRows) updates.push({ key: KEY_MAX_ROWS, value: maxRows })
    if (maxPerUser !== original.maxPerUser) {
      updates.push({ key: KEY_MAX_PER_USER, value: maxPerUser })
    }
    if (!sortedEqual(allowedPlans, original.allowedPlans)) {
      updates.push({ key: KEY_ALLOWED_PLANS, value: allowedPlans })
    }

    if (updates.length === 0) return

    // Calls the API directly (bypassing useUpdateConfig's mutation-level
    // onSuccess toast) so a partial failure across these independent
    // kb.* PATCHes can be reported truthfully in a single summary toast
    // instead of one misleading success toast per surviving field.
    setSaving(true)
    try {
      const results = await Promise.allSettled(
        updates.map((u) => api.patch(`/config/${u.key}`, { value: u.value }))
      )
      const failedKeys = results
        .map((r, i) => (r.status === 'rejected' ? updates[i].key : null))
        .filter((key): key is string => key !== null)

      if (failedKeys.length > 0) {
        toast.error(`No se pudo guardar: ${failedKeys.join(', ')}`)
      } else {
        toast.success('Configuración actualizada')
      }
    } finally {
      setSaving(false)
      queryClient.invalidateQueries({ queryKey: ['config'] })
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-96" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bases de conocimiento</CardTitle>
        <CardDescription>
          Limites y planes habilitados para subir archivos Excel como fuente de conocimiento en el
          chat
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-6 md:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="kb-max-file-mb">Tamaño máximo de archivo (MB)</Label>
            <Input
              id="kb-max-file-mb"
              type="number"
              min={1}
              max={KB_CEILING_FILE_MB}
              value={maxFileMb}
              onChange={(e) => setMaxFileMb(Number(e.target.value))}
              disabled={saving}
            />
            <p className="text-sm text-muted-foreground">
              Tope de la plataforma: {KB_CEILING_FILE_MB} MB
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="kb-max-rows">Filas máximas por archivo</Label>
            <Input
              id="kb-max-rows"
              type="number"
              min={1}
              max={KB_CEILING_ROWS}
              value={maxRows}
              onChange={(e) => setMaxRows(Number(e.target.value))}
              disabled={saving}
            />
            <p className="text-sm text-muted-foreground">
              Tope de la plataforma: {KB_CEILING_ROWS.toLocaleString('es-ES')} filas
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="kb-max-per-user">Bases por usuario</Label>
            <Input
              id="kb-max-per-user"
              type="number"
              min={1}
              max={KB_CEILING_PER_USER}
              value={maxPerUser}
              onChange={(e) => setMaxPerUser(Number(e.target.value))}
              disabled={saving}
            />
            <p className="text-sm text-muted-foreground">
              Tope de la plataforma: {KB_CEILING_PER_USER}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Planes habilitados para subir bases propias</Label>
          <div className="flex flex-wrap gap-3">
            {planOptions.map((plan) => (
              <label key={plan} className="flex items-center gap-2 text-sm capitalize">
                <Checkbox
                  checked={allowedPlans.includes(plan)}
                  onCheckedChange={() => togglePlan(plan)}
                  disabled={saving}
                />
                {plan}
              </label>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">
            Las bases de tipo sistema (pantalla "Bases de conocimiento") no dependen de este plan
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={!hasChanges || saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Guardar cambios
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
